import {inject, Injectable, signal} from '@angular/core';
import {collection, Firestore, getDocs} from 'firebase/firestore';
import {FirebaseInitService} from '../shared/firebase-init.service';
import {Practice} from '@models';
import {StorageService} from '../shared';

@Injectable()
export class PracticesService {
	public readonly practices = signal<Practice[]>([]);
	private readonly firebaseInit = inject(FirebaseInitService);
	private readonly storageService = inject(StorageService);
	private hasLoaded = false;
	
	// Lazy getter for Firebase Firestore to avoid initialization errors in SSR
	private get firestore(): Firestore {
		return this.firebaseInit.firestore;
	}
	
	private get collection() {
		return collection(this.firestore, 'practices');
	}
	private readonly cacheKey = 'practicesCache';
	private readonly cacheTtlMs = 180 * 24 * 60 * 60 * 1000;

	getPracticeList() {
		const cached = this.readCache();
		if (cached && cached.length > 0) {
			this.practices.set(cached);
		}
		if (this.hasLoaded) {
			return this.practices.asReadonly();
		}
		this.hasLoaded = true;
		const practisseList: Practice[] = [];
		(getDocs(this.collection)).then((datas: { forEach: (callback: (doc: { data: () => Record<string, unknown>; id: string; metadata: unknown }) => void) => void }) => {
			datas.forEach((doc: { data: () => Record<string, unknown>; id: string; metadata: unknown }) => {
				const data = doc.data();
				if (!data) {
					return;
				}
				const practice = new Practice(data);
				practice.uuid = doc.id;
				practice.metadata = doc.metadata;
				practisseList.push(practice);
			});
			this.practices.set(practisseList);
			this.writeCache(practisseList);
		});
		return this.practices.asReadonly();
	}

	private readCache(): Practice[] | null {
		const raw = this.storageService.get(this.cacheKey);
		if (!raw) {
			return null;
		}
		try {
			const payload = JSON.parse(raw) as { timestamp: number; practices: Array<{ uuid: string; Name: string }> };
			if (!payload || !Array.isArray(payload.practices) || !payload.timestamp) {
				return null;
			}
			if (Date.now() - payload.timestamp > this.cacheTtlMs) {
				return null;
			}
			return payload.practices.map((item) => {
				const practice = new Practice({Name: item.Name});
				practice.uuid = item.uuid;
				return practice;
			});
		} catch {
			return null;
		}
	}

	private writeCache(practices: Practice[]): void {
		const payload = {
			timestamp: Date.now(),
			practices: practices.map((practice) => ({
				uuid: practice.uuid,
				Name: practice.Name
			}))
		};
		this.storageService.set(this.cacheKey, JSON.stringify(payload));
	}
}
